import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import groceryService from '../services/groceryService';
import GroceryItemList from '../components/GroceryItemList';
import GroceryCartSummary from '../components/GroceryCartSummary';
import GroceryDonorForm from '../components/GroceryDonorForm';
import GroceryPaymentStep from '../components/GroceryPaymentStep';
import {
  STEPS,
  DONATION_TYPES,
  PAYMENT_METHODS,
  FREQUENCIES,
  DEFAULT_DONOR,
  DEFAULT_CARD,
  DEFAULT_ACH,
  DEFAULT_RECURRING,
  TEMPLE_ADDRESS,
  formatCurrency,
  calculateTotal,
  getSelectedItems,
} from '../models/groceryModels';
import { validateDonor, validatePaymentStep, hasErrors } from '../models/groceryValidation';
import './DonateGrocery.css';

const DonateGrocery = () => {
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [currentStep, setCurrentStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [stepError, setStepError] = useState('');
  const [errors, setErrors] = useState({});
  const [result, setResult] = useState(null);

  const [donor, setDonor] = useState(DEFAULT_DONOR);

  const [donationType, setDonationType] = useState(DONATION_TYPES.ONLINE);
  const [paymentMethod, setPaymentMethod] = useState(PAYMENT_METHODS.CARD);
  const [frequency, setFrequency] = useState(FREQUENCIES.ONETIME);
  const [card, setCard] = useState(DEFAULT_CARD);
  const [ach, setAch] = useState(DEFAULT_ACH);
  const [recurring, setRecurring] = useState(DEFAULT_RECURRING);
  const [dropOff, setDropOff] = useState({ date: '', time: '' });
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const [showSummarySheet, setShowSummarySheet] = useState(false);

  const abortRef = useRef(null);
  const recaptchaRef = useRef(null);

  /* ── Load items (cancelled on unmount) ───────────────────────────────── */

  const loadItems = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setItemsLoading(true);
    setLoadError('');

    try {
      const data = await groceryService.getGroceryItems({ signal: controller.signal });
      setItems(data.map((item) => ({ ...item, quantity: 0 })));
    } catch (error) {
      if (groceryService.isCancel(error)) return;
      setLoadError('Unable to load grocery items. Please try again.');
    } finally {
      if (!controller.signal.aborted) setItemsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadItems();
    return () => abortRef.current?.abort();
  }, [loadItems]);

  // The summary sheet is step-specific, so close it whenever the step changes.
  useEffect(() => {
    setShowSummarySheet(false);
  }, [currentStep]);

  // The sheet is mobile-only; closing it on resize avoids leaving the body
  // scroll locked while CSS hides the sheet at desktop widths.
  useEffect(() => {
    const query = window.matchMedia('(min-width: 1024px)');
    const handler = (event) => {
      if (event.matches) setShowSummarySheet(false);
    };
    query.addEventListener('change', handler);
    return () => query.removeEventListener('change', handler);
  }, []);

  // Stop the page behind the sheet from scrolling while it is open.
  useEffect(() => {
    if (!showSummarySheet) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [showSummarySheet]);

  /* ── Derived values ──────────────────────────────────────────────────── */

  const selectedItems = useMemo(() => getSelectedItems(items), [items]);
  const totalAmount = useMemo(() => calculateTotal(selectedItems), [selectedItems]);

  const isPhysical = donationType === DONATION_TYPES.PHYSICAL;
  const isRecurring = !isPhysical && frequency === FREQUENCIES.RECURRING;

  const stepLabels = useMemo(
    () => STEPS.map((step) => (step.id === 3 && isPhysical ? { ...step, label: 'Drop-Off' } : step)),
    [isPhysical]
  );

  /* ── Handlers ────────────────────────────────────────────────────────── */

  const handleQuantityChange = useCallback((itemId, quantity) => {
    setItems((prev) => prev.map((item) => (item.itemId === itemId ? { ...item, quantity } : item)));
    setStepError('');
  }, []);

  const handleDonorChange = useCallback((field, value) => {
    setDonor((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));
  }, []);

  const handleCardChange = useCallback((field, value) => {
    setCard((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));
  }, []);

  const handleAchChange = useCallback((field, value) => {
    setAch((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));
  }, []);

  const handleRecurringChange = useCallback((field, value) => {
    setRecurring((prev) => ({ ...prev, [field]: value }));
    setErrors({});
  }, []);

  const handleDropOffChange = useCallback((field, value) => {
    setDropOff((prev) => ({ ...prev, [field]: value }));
    setErrors({});
  }, []);

  const handleDonationTypeChange = useCallback((value) => {
    setDonationType(value);
    setErrors({});
  }, []);

  const handleRecaptchaChange = useCallback((token) => {
    setRecaptchaToken(token);
    setErrors((prev) => (prev.recaptcha ? { ...prev, recaptcha: undefined } : prev));
  }, []);

  // A verified token is single-use, so clear it after every attempt.
  const resetRecaptcha = useCallback(() => {
    setRecaptchaToken('');
    recaptchaRef.current?.reset();
  }, []);

  /* ── Navigation ──────────────────────────────────────────────────────── */

  const goNext = useCallback(() => {
    setStepError('');

    if (currentStep === 1) {
      if (selectedItems.length === 0) {
        setStepError('Please add at least one item using the + button before continuing.');
        return;
      }
      setCurrentStep(2);
      return;
    }

    if (currentStep === 2) {
      const donorErrors = validateDonor(donor);
      setErrors(donorErrors);
      if (hasErrors(donorErrors)) {
        setStepError('Please correct the highlighted fields.');
        return;
      }
      setCurrentStep(3);
    }
  }, [currentStep, selectedItems.length, donor]);

  const goBack = useCallback(() => {
    setStepError('');
    setCurrentStep((step) => Math.max(1, step - 1));
  }, []);

  const resetFlow = useCallback(() => {
    setResult(null);
    setCurrentStep(1);
    setDonor(DEFAULT_DONOR);
    setCard(DEFAULT_CARD);
    setAch(DEFAULT_ACH);
    setRecurring(DEFAULT_RECURRING);
    setDropOff({ date: '', time: '' });
    setDonationType(DONATION_TYPES.ONLINE);
    setPaymentMethod(PAYMENT_METHODS.CARD);
    setFrequency(FREQUENCIES.ONETIME);
    setErrors({});
    setItems((prev) => prev.map((item) => ({ ...item, quantity: 0 })));
    resetRecaptcha();
  }, [resetRecaptcha]);

  /* ── Submit ──────────────────────────────────────────────────────────── */

  const handleSubmit = useCallback(async () => {
    setStepError('');
    setShowSummarySheet(false);

    const stepErrors = validatePaymentStep({
      donationType,
      paymentMethod,
      frequency,
      card,
      ach,
      recurring,
      dropOff,
      recaptchaToken,
    });
    setErrors(stepErrors);

    if (hasErrors(stepErrors)) {
      setStepError('Please correct the highlighted fields.');
      return;
    }

    if (selectedItems.length === 0) {
      setStepError('Your donation is empty. Please add at least one item.');
      return;
    }

    setSubmitting(true);

    try {
      const verification = await groceryService.verifyRecaptcha(recaptchaToken);
      if (verification?.success !== true) {
        setStepError('reCAPTCHA verification failed. Please tick the checkbox again.');
        return;
      }

      if (isPhysical) {
        const payload = groceryService.buildDonationPayload({
          donor,
          selectedItems,
          totalAmount,
          isPhysical: true,
          dropOff,
        });

        const response = await groceryService.addGroceryDonation(payload);

        if (!response?.success && !response?.Success) {
          setStepError('We could not schedule your drop-off. Please try again.');
          return;
        }

        await groceryService.sendGroceryDonationEmail(payload);

        setResult({
          success: true,
          title: 'Drop-Off Scheduled',
          message: `Thank you, ${donor.firstName}. Please bring your items on ${dropOff.date} during ${dropOff.time}.`,
          detail: `${TEMPLE_ADDRESS.name}, ${TEMPLE_ADDRESS.street}`,
        });
        return;
      }

      const authNetPayload = groceryService.buildAuthNetPayload({
        donor,
        card,
        ach,
        selectedItems,
        totalAmount,
        paymentMethod,
        isRecurring,
        recurring,
      });

      const paymentResponse = isRecurring
        ? await groceryService.processRecurringPayment(authNetPayload, '0')
        : await groceryService.processOneTimePayment(authNetPayload);

      const outcome = groceryService.extractPaymentResult(paymentResponse);

      if (!outcome.approved) {
        setResult({
          success: false,
          title: 'Payment Declined',
          message: outcome.errorText || 'Your payment could not be processed. Please check your details and try again.',
        });
        return;
      }

      const payload = groceryService.buildDonationPayload({
        donor,
        selectedItems,
        totalAmount,
        isPhysical: false,
        paymentMethod,
        isRecurring,
        transactionId: outcome.transactionId,
        subscriptionId: outcome.subscriptionId,
      });

      const dbResponse = await groceryService.addGroceryDonation(payload);

      if (!dbResponse?.success && !dbResponse?.Success) {
        setResult({
          success: false,
          title: 'Payment Taken, Record Failed',
          message:
            'Your payment was processed but we could not save the donation record. Please contact the temple office with your transaction ID.',
          detail: outcome.transactionId ? `Transaction ID: ${outcome.transactionId}` : '',
        });
        return;
      }

      await groceryService.sendGroceryDonationEmail(payload);

      setResult({
        success: true,
        title: 'Thank You for Your Donation',
        message: `Your ${isRecurring ? 'monthly ' : ''}grocery donation of ${formatCurrency(totalAmount)} was received. A confirmation email is on its way to ${donor.email}.`,
        detail: outcome.transactionId ? `Transaction ID: ${outcome.transactionId}` : '',
      });
    } catch (error) {
      setStepError(
        error?.response?.data?.message ||
          'Something went wrong while processing your donation. Please try again.'
      );
    } finally {
      setSubmitting(false);
      resetRecaptcha();
    }
  }, [
    donationType,
    paymentMethod,
    frequency,
    card,
    ach,
    recurring,
    dropOff,
    donor,
    selectedItems,
    totalAmount,
    isPhysical,
    isRecurring,
    recaptchaToken,
    resetRecaptcha,
  ]);

  /* ── Render ──────────────────────────────────────────────────────────── */

  const progressWidth = `${((currentStep - 1) / (STEPS.length - 1)) * 100}%`;

  const backButton =
    currentStep > 1 ? (
      <button
        type="button"
        className="grocery-btn grocery-btn-secondary"
        onClick={goBack}
        disabled={submitting}
      >
        &larr; Back
      </button>
    ) : null;

  const primaryButton =
    currentStep < 3 ? (
      <button type="button" className="grocery-btn grocery-btn-primary" onClick={goNext}>
        Next &rarr;
      </button>
    ) : (
      <button
        type="button"
        className="grocery-btn grocery-btn-primary"
        onClick={handleSubmit}
        disabled={submitting}
      >
        {isPhysical ? 'Schedule Drop-Off' : `Donate ${formatCurrency(totalAmount)}`}
      </button>
    );

  const actionBar = (
    <div className="grocery-actions">
      {backButton || <span />}

      {selectedItems.length > 0 && (
        <div className="grocery-actions-total">
          <span className="grocery-actions-total-amount">{formatCurrency(totalAmount)}</span>
          <span className="grocery-actions-total-count">
            {selectedItems.length} item{selectedItems.length === 1 ? '' : 's'}
          </span>
        </div>
      )}

      {primaryButton}
    </div>
  );

  // Mobile gets the same actions pinned to the bottom, matching the Tiffin
  // pattern; CSS keeps only one of the two bars visible per breakpoint.
  const mobileBar = (
    <div className="grocery-mobile-bar">
      {selectedItems.length > 0 && (
        <button
          type="button"
          className="grocery-mobile-bar-info"
          onClick={() => setShowSummarySheet(true)}
          aria-expanded={showSummarySheet}
        >
          <span className="grocery-mobile-bar-total">{formatCurrency(totalAmount)}</span>
          <span className="grocery-mobile-bar-count">
            {selectedItems.length} item{selectedItems.length === 1 ? '' : 's'} selected
            <span className="grocery-mobile-bar-caret" aria-hidden="true">
              {'\u25B2'}
            </span>
          </span>
        </button>
      )}

      <div className="grocery-mobile-bar-actions">
        {backButton}
        {primaryButton}
      </div>
    </div>
  );

  const summarySheet = (
    <div className="grocery-sheet-layer" role="dialog" aria-modal="true" aria-label="Donation Summary">
      <div className="grocery-sheet-backdrop" onClick={() => setShowSummarySheet(false)} />

      <div className="grocery-sheet">
        <div className="grocery-sheet-handle" />

        <div className="grocery-sheet-header">
          <span className="grocery-sheet-title">Donation Summary</span>
          <button
            type="button"
            className="grocery-sheet-close"
            onClick={() => setShowSummarySheet(false)}
            aria-label="Close summary"
          >
            &times;
          </button>
        </div>

        <div className="grocery-sheet-body">
          <GroceryCartSummary selectedItems={selectedItems} totalAmount={totalAmount} />
        </div>

        <div className="grocery-sheet-footer">{primaryButton}</div>
      </div>
    </div>
  );

  return (
    <div className="grocery-page">
      <div className="grocery-container">
        <button type="button" className="grocery-back-btn" onClick={() => navigate('/')}>
          &larr; Back to Home
        </button>

        <div className="grocery-header">
          <h1 className="grocery-title">Donate Groceries</h1>
          <p className="grocery-subtitle">
            Support the temple kitchen by donating groceries or contributing their value.
          </p>
        </div>

        <div className="grocery-stepper">
          <div className="grocery-stepper-track" />
          <div className="grocery-stepper-progress" style={{ width: progressWidth }} />
          {stepLabels.map((step) => {
            const state =
              currentStep > step.id ? 'is-complete' : currentStep === step.id ? 'is-active' : '';
            return (
              <div className={`grocery-step ${state}`} key={step.id}>
                <div className="grocery-step-circle">
                  {currentStep > step.id ? '\u2713' : step.id}
                </div>
                <div className="grocery-step-label">{step.label}</div>
              </div>
            );
          })}
        </div>

        {loadError && (
          <div className="grocery-banner grocery-banner-error">
            {loadError}
            <button type="button" className="grocery-banner-retry" onClick={loadItems}>
              Retry
            </button>
          </div>
        )}

        {stepError && <div className="grocery-banner grocery-banner-warn">{stepError}</div>}

        {itemsLoading ? (
          <div className="grocery-loading">
            <div className="grocery-spinner" />
            <p className="grocery-loading-text">Loading grocery items...</p>
          </div>
        ) : items.length === 0 && !loadError ? (
          <div className="grocery-empty">No grocery items are available right now.</div>
        ) : (
          <div className="grocery-layout">
            <div className="grocery-main">
              <div className="grocery-panel">
                {actionBar}

                {currentStep === 1 && (
                  <>
                    <div className="grocery-panel-title">Select the items you would like to donate</div>
                    <GroceryItemList items={items} onQuantityChange={handleQuantityChange} />
                  </>
                )}

                {currentStep === 2 && (
                  <>
                    <div className="grocery-panel-title">Your Information</div>
                    <GroceryDonorForm donor={donor} errors={errors} onChange={handleDonorChange} />
                  </>
                )}

                {currentStep === 3 && (
                  <>
                    <div className="grocery-panel-title">
                      {isPhysical ? 'Drop-Off Details' : 'Payment Details'}
                    </div>
                    <GroceryPaymentStep
                      donationType={donationType}
                      paymentMethod={paymentMethod}
                      frequency={frequency}
                      card={card}
                      ach={ach}
                      recurring={recurring}
                      dropOff={dropOff}
                      errors={errors}
                      onDonationTypeChange={handleDonationTypeChange}
                      onPaymentMethodChange={setPaymentMethod}
                      onFrequencyChange={setFrequency}
                      onCardChange={handleCardChange}
                      onAchChange={handleAchChange}
                      onRecurringChange={handleRecurringChange}
                      onDropOffChange={handleDropOffChange}
                      onRecaptchaChange={handleRecaptchaChange}
                      recaptchaRef={recaptchaRef}
                    />
                  </>
                )}
              </div>
            </div>

            <div className="grocery-sidebar">
              <GroceryCartSummary selectedItems={selectedItems} totalAmount={totalAmount} />
            </div>
          </div>
        )}

        {!itemsLoading && items.length > 0 && <div className="grocery-mobile-spacer" />}
      </div>

      {!itemsLoading && items.length > 0 && !result && mobileBar}

      {showSummarySheet && summarySheet}

      {submitting && (
        <div className="grocery-processing-overlay">
          <div className="grocery-processing-inner">
            <div className="grocery-processing-rings">
              <span className="grocery-ring grocery-ring-1" />
              <span className="grocery-ring grocery-ring-2" />
              <div className="grocery-processing-logo">
                <img
                  src="/yk-logo.png"
                  alt="Yugal Kunj"
                  onError={(event) => {
                    event.target.style.display = 'none';
                  }}
                />
              </div>
            </div>
            <h3 className="grocery-processing-title">
              {isPhysical ? 'Scheduling Your Drop-Off' : 'Processing Payment'}
            </h3>
            <p className="grocery-processing-sub">Please wait &middot; do not close this window</p>
            <div className="grocery-processing-dots">
              {[0, 150, 300].map((delay) => (
                <span
                  key={delay}
                  className="grocery-processing-dot"
                  style={{ animationDelay: `${delay}ms` }}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {result && (
        <div className="grocery-modal-overlay" role="dialog" aria-modal="true">
          <div className="grocery-modal">
            <div className="grocery-modal-icon">{result.success ? '\u2705' : '\u26A0\uFE0F'}</div>
            <div className="grocery-modal-title">{result.title}</div>
            <div className="grocery-modal-body">
              {result.message}
              {result.detail && (
                <>
                  <br />
                  <br />
                  <strong>{result.detail}</strong>
                </>
              )}
            </div>
            <div className="grocery-modal-actions">
              {result.success ? (
                <>
                  <button
                    type="button"
                    className="grocery-btn grocery-btn-secondary"
                    onClick={resetFlow}
                  >
                    Donate Again
                  </button>
                  <button
                    type="button"
                    className="grocery-btn grocery-btn-primary"
                    onClick={() => navigate('/')}
                  >
                    Done
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="grocery-btn grocery-btn-primary"
                  onClick={() => setResult(null)}
                >
                  Try Again
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DonateGrocery;
